import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api/axios'
import type { ScanEstimate } from '@/lib/utils/scan-estimate'

/** A requested scan of one version, as /api/scan-requests and the versions list send it. */
export interface ScanRequest {
    id: number
    versionId: number
    objectId: string
    objectName: string
    reason: 'manual' | 'retry' | 'pre_recovery'
    recoverRequestId: string | null
    state: 'queued' | 'sent' | 'copying' | 'launched' | 'scanning' | 'complete' | 'failed' | 'cancelled'
    open: boolean
    requestedByName: string | null
    requestedAt: string
    finishedAt: string | null
    resultStatus: string | null
    error: string | null
}

/** A version's scan requests: the one under way, and how the last one ended. */
export interface VersionScanRequests { open: ScanRequest | null; last: ScanRequest | null }

export interface ScanSettings { defaultOn: boolean; maxAgeHours: number }

export const scanRequestsApi = {
    request: (versionId: number) => api.post<{ data: ScanRequest }>('/api/scan-requests', { versionId }),
    estimate: (versionIds: number[]) => api.get<{ data: ScanEstimate }>('/api/scan-requests/estimate', { params: { versionId: versionIds } }),
    settings: () => api.get<{ data: ScanSettings }>('/api/scan-requests/settings'),
}

/** Ask for a version to be scanned again. Refreshes the versions list, which carries the request. */
export function useRequestScan() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (versionId: number) => scanRequestsApi.request(versionId).then((r) => r.data.data),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['objects'] }) },
    })
}

/** How long scanning these versions is expected to take. Asked only when `enabled`. */
export function useScanEstimate(versionIds: Array<number | null | undefined>, enabled = true) {
    const ids = [...new Set(versionIds.filter((id): id is number => typeof id === 'number' && id > 0))].sort((a, b) => a - b)
    return useQuery({
        queryKey: ['scan-requests', 'estimate', ids],
        queryFn: () => scanRequestsApi.estimate(ids).then((r) => r.data.data),
        enabled: enabled && ids.length > 0,
        staleTime: 5 * 60_000,
    })
}

/** Is "Scan before copying to egress" offered on a recovery request, and ticked to begin with? */
export function useScanSettings(enabled = true) {
    return useQuery({
        queryKey: ['scan-requests', 'settings'],
        queryFn: () => scanRequestsApi.settings().then((r) => r.data.data),
        enabled,
        staleTime: 5 * 60_000,
    })
}
